import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { AGENTS_PATH } from '../../config';
import { splitLabelled } from '../../content';
import { cardSurfaceSx, gridGapSx, tokens } from '../../theme';
import { AiToolChip } from '../AiToolRow';
import type { AiAgentLink, AiAugmentationProps } from './types';

/**
 * What AI does for this method, what it is done with, and what must not leave the
 * building.
 *
 * **Three cards, and the third is deliberately not like the other two.** The ways
 * of working are guardrails, so they sit on the `accent.info*` trio — a tinted
 * ground, a matching edge and a coloured label, which is why that treatment has
 * tokens of its own.
 *
 * **Blue rather than red, and the distance from red is the point.** Red is both
 * the action colour and the error register: `ErrorState` draws
 * `accent.brandSurface` under a `primary.main` glyph, and it renders on this
 * very page whenever a request fails. Guidance and a failure reading alike is
 * what this treatment exists to avoid.
 *
 * **The guardrail descriptions take `text.meta`, not `text.secondary`.** The
 * tint costs contrast the plain cards do not pay: `text.secondary` is 4.08:1 on
 * `infoSurface` against 4.41:1 on `surface.raised`, and `text.meta` clears AA at
 * 4.53:1. The other two cards are left alone deliberately — `text.secondary`
 * misses AA on `surface.raised` too, but that is every card in the app rather
 * than this one, and it is not this component's to fix.
 *
 * **The whole block disappears when a method has none of it.** A third of method
 * pages have no AI Augmentation section, so a heading over three empty cards
 * would be the normal case rather than the exception.
 */

/** A bulleted list of entries, each split on its first colon where it has one. */
const LabelledList = ({
  items,
  descriptionColour = 'text.secondary',
}: {
  items: string[];
  descriptionColour?: string;
}) => (
  <Box
    component="ul"
    sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
  >
    {items.map((item) => {
      const { label, description } = splitLabelled(item);

      return (
        <Box component="li" key={item}>
          <Typography
            component="p"
            sx={{
              fontFamily: tokens.fontFamily.heading,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
              color: 'text.primary',
            }}
          >
            {label}
          </Typography>

          {description !== undefined && (
            <Typography variant="body2" sx={{ color: descriptionColour, marginTop: '2px' }}>
              {description}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

/**
 * The Agent Skills that help with this method, each a link to its own page with what it
 * is for beneath. **The title is the link and the card is not**: the card holds
 * several destinations, so it stays flat, and the one a reader wants is the name.
 */
const AgentLinks = ({ agents }: { agents: AiAgentLink[] }) => (
  <Box
    component="ul"
    sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
  >
    {agents.map((agent) => (
      <Box component="li" key={agent.name}>
        <RouterLink
          to={`${AGENTS_PATH}/${encodeURIComponent(agent.name)}`}
          style={{ textDecoration: 'none' }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: tokens.fontFamily.heading,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.4,
              color: 'text.link',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {agent.title}
          </Typography>
        </RouterLink>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginTop: '2px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
          }}
        >
          {agent.summary}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * Three columns for up to three cards, which is every layout this block drew
 * before Agent Skills. **Four cards is the only case that changes**: a fourth in a
 * three-column grid would sit alone on a second row, so it takes four columns
 * where there is room and two where there is not.
 */
const COLUMNS = {
  three: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  four: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
} as const;

/** A card in the block. Titled, and sized by its own content. */
const AugmentationCard = ({
  heading,
  caution = false,
  children,
}: {
  heading: string;
  caution?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      ...cardSurfaceSx,
      ...(caution
        ? { backgroundColor: 'accent.infoSurface', borderColor: 'accent.infoBorder', boxShadow: 2 }
        : {}),
      padding: 6,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      height: '100%',
    }}
  >
    <Typography
      variant={caution ? 'overline' : 'h3'}
      component="h3"
      sx={caution ? { color: 'accent.info', letterSpacing: '1px' } : undefined}
    >
      {heading}
    </Typography>

    {children}
  </Box>
);

export const AiAugmentation = ({ practices, tools, waysOfWorking, agents }: AiAugmentationProps) => {
  const hasPractices = practices !== undefined && practices.length > 0;
  const hasTools = tools !== undefined && tools.length > 0;
  const hasWays = waysOfWorking !== undefined && waysOfWorking.length > 0;
  const hasAgents = agents !== undefined && agents.length > 0;

  if (!hasPractices && !hasTools && !hasWays && !hasAgents) {
    return null;
  }

  const cards = [hasPractices, hasTools, hasWays, hasAgents].filter(Boolean).length;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: cards === 4 ? COLUMNS.four : COLUMNS.three,
        ...gridGapSx,
        alignItems: 'start',
      }}
    >
      {hasPractices && (
        <AugmentationCard heading="AI Practices">
          <LabelledList items={practices} />
        </AugmentationCard>
      )}

      {hasTools && (
        <AugmentationCard heading="AI Tooling">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {tools.map((tool) => (
              <AiToolChip key={tool} label={tool} />
            ))}
          </Box>
        </AugmentationCard>
      )}

      {hasAgents && (
        <AugmentationCard heading="Agent Skills">
          <AgentLinks agents={agents} />
        </AugmentationCard>
      )}

      {hasWays && (
        <AugmentationCard heading="AI Ways of Working" caution>
          <LabelledList items={waysOfWorking} descriptionColour="text.meta" />
        </AugmentationCard>
      )}
    </Box>
  );
};
